import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, Tabs, type Tab } from '@node-space/ui-components'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import {
  formatOwnerLabel,
  formatPaymentMethodLabel,
  formatZoneOffset,
  LIMIT_TYPE_LABELS,
} from 'pages/PaymentLimits/utils'
import { LimitType, type Limit } from 'types/paymentLimits'
import { formatNumberWithCommas } from 'utils/utils'
import { PaymentLimitActivityTab } from './PaymentLimitActivityTab'

export interface PaymentLimitSidePanelTitleKeys {
  currency: string
  paymentMethod: string
  limitType: string
}

interface PaymentLimitSidePanelHeaderProps {
  limit: Limit | null
}

interface PaymentLimitSidePanelBodyProps {
  limit: Limit | null
  titleKeys: PaymentLimitSidePanelTitleKeys
}

export const PaymentLimitSidePanelHeader = ({ limit }: PaymentLimitSidePanelHeaderProps) => {
  const iconUrl = limit?.currency
    ? `${process.env.ICONS_URL}${limit?.currency?.toLowerCase()}.svg`
    : undefined
  const iconUrls = iconUrl ? [iconUrl] : []

  const amountLabel = limit ? `${formatNumberWithCommas(limit?.amount)} ${limit?.currency}` : ''

  return (
    <SidePanelHeaderTemplateTwo
      heading={{
        iconUrls,
        secondaryLabel: limit
          ? formatPaymentMethodLabel(limit.method ?? limit.qualifiers?.method ?? '')
          : '',
        primaryLabel: amountLabel,
      }}
    />
  )
}

const PaymentLimitOverviewTab = ({ limit, titleKeys }: PaymentLimitSidePanelBodyProps) => {
  const { t } = useTranslation()

  return (
    <Box flexCol width="full">
      <DataListItem id="currency" title={t(titleKeys.currency)} body={limit?.currency ?? ''} />
      <DataListItem
        id="payment-method"
        title={t(titleKeys.paymentMethod)}
        body={limit?.method ? formatPaymentMethodLabel(limit.method) : ''}
      />
      <DataListItem
        id="limit-type"
        title={t(titleKeys.limitType)}
        body={limit ? t(LIMIT_TYPE_LABELS[limit?.limitType] ?? limit?.limitType) : ''}
      />
      <DataListItem
        id="owner"
        title={t('paymentLimits.owner')}
        body={limit?.owner ? formatOwnerLabel(limit.owner) : ''}
      />
      {limit?.limitType === LimitType.DAILY && (
        <DataListItem
          id="zone-offset"
          title={t('paymentLimits.timezone')}
          body={formatZoneOffset(limit?.zoneOffset)}
        />
      )}
    </Box>
  )
}

export const PaymentLimitSidePanelBody = ({ limit, titleKeys }: PaymentLimitSidePanelBodyProps) => {
  const { t } = useTranslation()

  const tabs: Tab[] = [
    {
      id: 'overview',
      header: t('paymentLimits.activity.overview'),
      content: <PaymentLimitOverviewTab limit={limit} titleKeys={titleKeys} />,
    },
    {
      id: 'activity',
      header: t('paymentLimits.activity.activity'),
      content: <PaymentLimitActivityTab limitId={limit?.id} />,
    },
  ]

  return (
    <Box width="full">
      <Tabs tabs={tabs} testId="payment-limit-side-panel-tabs" />
    </Box>
  )
}
